/**
 * @voltxampere/presets — Op-Amp Astable / 555 Alternative (#16)
 *
 * Sprint 23 H1 + ADR-0048 closure'ı. NE555 timer'ın discrete
 * IC modeli yerine, op-amp Schmitt trigger feedback + RC timing
 * astable schematic'i — eğitsel olarak NE555'in temel davranışını
 * (sürekli kare dalga osilatörü) gösteren basit bir devre.
 *
 * Çalışma prensibi:
 *   - Op-amp inverting girişi V_t = V_C (RC kapasitörünün gerilimi)
 *   - Non-inverting girişi V_+ = (R₂/(R₁+R₂)) · V_out
 *   - Op-amp positive feedback ile Schmitt trigger:
 *     V_out doyumda (±V_sat ≈ ±10.5V at 12V supply)
 *   - V_t > V_+ olunca op-amp output negatife döner → C_t deşarj olur
 *   - V_t < V_+ olunca op-amp output pozitife döner → C_t şarj olur
 *   - Frekans: f = 1 / (2·R_t·C_t·ln(3)) ≈ 1 / (2·10k·10n·1.0986) ≈ 4554 Hz
 *
 * Bu eğitsel preset; gerçek NE555 IC'sinin discrete modeli (3 transistor +
 * 2 comparator + RS flip-flop, ~25 component) Sprint 24+'a ertelendi.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const opampAstable = {
    id: "opamp-astable",
    name: { tr: "Op-Amp Astable (555 alt.)", en: "Op-Amp Astable (555 alt.)" },
    category: "amplifier",
    difficulty: 4,
    description: {
        tr: "Op-amp Schmitt trigger + RC kapasitör → kare dalga osilatörü. Frekans = 1/(2·R·C·ln3) ≈ 4.55 kHz. NE555 timer'ın eğitsel alternatifi.",
        en: "Op-amp Schmitt trigger + RC capacitor → square-wave oscillator. Frequency = 1/(2·R·C·ln3) ≈ 4.55 kHz. Educational NE555 alternative.",
    },
    // Sprint 25 R2 lane 4D-A — hand-authored layout. V1 far left
    // (vcc rail dangles since the op-amp model has no Vcc terminal —
    // intentional schema gap, see preset preamble). U1 body centered
    // at (60,0). Two feedback paths:
    //   - Schmitt hysteresis (positive feedback) below the op-amp:
    //     out → R1 → vplus → R2 → gnd, with R1 horizontal at y=100
    //     and R2 horizontal at y=100 to its left.
    //   - Timing RC (negative feedback) above the op-amp:
    //     out → Rt → vt → Ct → gnd, mirrored at y=-100.
    // out fans out via a vertical bus at x=140; gnd collects ct/r2
    // returns via an x=-200 bus that meets the V1 gnd rail at
    // (-360,91) = GND1.p.
    circuit: makeCircuit({
        slot: 16,
        name: "Op-Amp Astable",
        description: "555 timer alternative — Schmitt trigger + RC astable",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "out" }),
            makeNet({ name: "vplus" }), // op-amp non-inverting input (positive feedback divider)
            makeNet({ name: "vt" }), // op-amp inverting input (timing capacitor)
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 12 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V1",
                position: { x: -360, y: 0 },
                rotation: 0,
            }),
            // Positive feedback divider: V_+ = R2/(R1+R2) · V_out = 0.5 · V_out
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 10_000 },
                terminals: { p1: "net_out", p2: "net_vplus" },
                label: "R1",
                position: { x: 60, y: 100 },
                rotation: 180,
            }),
            makeComponent({
                id: "R2",
                type: "resistor",
                params: { resistance: 10_000 },
                terminals: { p1: "net_vplus", p2: "net_gnd" },
                label: "R2",
                position: { x: -40, y: 100 },
                rotation: 0,
            }),
            // Timing RC: R_t between V_out and V_t (capacitor node), C_t to gnd.
            makeComponent({
                id: "Rt",
                type: "resistor",
                params: { resistance: 10_000 },
                terminals: { p1: "net_out", p2: "net_vt" },
                label: "Rt",
                position: { x: 60, y: -100 },
                rotation: 180,
            }),
            makeComponent({
                id: "Ct",
                type: "capacitor",
                params: { capacitance: 10e-9 }, // 10 nF
                terminals: { p1: "net_vt", p2: "net_gnd" },
                label: "Ct",
                position: { x: -40, y: -100 },
                rotation: 0,
            }),
            // Op-amp: in_pos = vplus (positive feedback), in_neg = vt
            // (timing voltage), out = out.
            makeComponent({
                id: "U1",
                type: "opamp",
                params: { openLoopGain: 2e5, saturationVoltage: 10.5 }, // µA741-like
                terminals: { in_pos: "net_vplus", in_neg: "net_vt", out: "net_out" },
                label: "U1",
                position: { x: 60, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -360, y: 100 },
                rotation: 0,
            }),
        ],
        wires: [
            // net_out fan-out leg A: U1.out (100,0) → Rt.p1 (80,-100) via
            // vertical bus at x=140.
            {
                id: "w_out_rt",
                netId: "net_out",
                path: [
                    { x: 100, y: 0 },
                    { x: 140, y: 0 },
                    { x: 140, y: -100 },
                    { x: 80, y: -100 },
                ],
                style: "orthogonal",
            },
            // net_out fan-out leg B: x=140 bus → R1.p1 (80,100). Junction
            // at (140,0) is implied by the shared netId.
            {
                id: "w_out_r1",
                netId: "net_out",
                path: [
                    { x: 140, y: 0 },
                    { x: 140, y: 100 },
                    { x: 80, y: 100 },
                ],
                style: "orthogonal",
            },
            // net_vt leg A: in_neg (40,-4) → Rt.p2 (40,-100). Vertical
            // drop at x=40.
            {
                id: "w_vt_in",
                netId: "net_vt",
                path: [
                    { x: 40, y: -4 },
                    { x: 40, y: -100 },
                ],
                style: "orthogonal",
            },
            // net_vt leg B: Rt.p2 (40,-100) → Ct.p1 (-60,-100). Horizontal
            // along y=-100.
            {
                id: "w_vt_ct",
                netId: "net_vt",
                path: [
                    { x: 40, y: -100 },
                    { x: -60, y: -100 },
                ],
                style: "orthogonal",
            },
            // net_vplus leg A: in_pos (40,4) → R1.p2 (40,100). Vertical
            // drop at x=40.
            {
                id: "w_vplus_in",
                netId: "net_vplus",
                path: [
                    { x: 40, y: 4 },
                    { x: 40, y: 100 },
                ],
                style: "orthogonal",
            },
            // net_vplus leg B: R1.p2 (40,100) → R2.p1 (-60,100). Horizontal
            // along y=100.
            {
                id: "w_vplus_r2",
                netId: "net_vplus",
                path: [
                    { x: 40, y: 100 },
                    { x: -60, y: 100 },
                ],
                style: "orthogonal",
            },
            // net_gnd leg A: V1.neg (-360,10) → GND1.p (-360,91). Main
            // gnd rail.
            {
                id: "w_gnd_v1",
                netId: "net_gnd",
                path: [
                    { x: -360, y: 10 },
                    { x: -360, y: 91 },
                ],
                style: "orthogonal",
            },
            // net_gnd leg B: Ct.p2 (-20,-100) → x=-200 bus → GND1.p
            // (-360,91).
            {
                id: "w_gnd_ct",
                netId: "net_gnd",
                path: [
                    { x: -20, y: -100 },
                    { x: -200, y: -100 },
                    { x: -200, y: 91 },
                    { x: -360, y: 91 },
                ],
                style: "orthogonal",
            },
            // net_gnd leg C: R2.p2 (-20,100) → x=-200 bus. Joins the bus
            // already wired to GND1 via the shared netId.
            {
                id: "w_gnd_r2",
                netId: "net_gnd",
                path: [
                    { x: -20, y: 100 },
                    { x: -200, y: 100 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
    // DC OP: op-amp metastable noktası — saturator herhangi bir
    // tarafa düşer. Pratikte initialState'i çözücüye bırakırız.
    // Frekans iddiası transient analizinde geçerli, DC OP testi yok.
    ],
    learningGoals: [
        {
            tr: "Schmitt trigger feedback + RC timing → kare dalga osilatörü.",
            en: "Schmitt trigger feedback + RC timing → square-wave oscillator.",
        },
        {
            tr: "Frekans: f = 1/(2·R·C·ln3). R=10kΩ, C=10nF → ~4.55 kHz.",
            en: "Frequency: f = 1/(2·R·C·ln3). R=10kΩ, C=10nF → ~4.55 kHz.",
        },
        {
            tr: "NE555 timer'ın eğitsel alternatifi — 555'in temel kavramını gösterir.",
            en: "Educational alternative to NE555 timer — illustrates the core 555 concept.",
        },
    ],
    references: [
        "TI SLOA060 — Op-amp astable multivibrator application note",
        "Horowitz/Hill, The Art of Electronics, §4.3 (relaxation oscillators)",
        "ADR-0048 (Sprint 23 H1)",
    ],
};
//# sourceMappingURL=opamp-astable.js.map